import { Component, HostBinding, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { ButtonComponent } from '../button/button.component';
import { RecommendedJob } from '../../models/job.model';
import { RankLabelPipe } from '../../../../../../libs/shared/src/lib/pipes/rank-label.pipe';
import { MatchScoreClassPipe } from '../../../../../../libs/shared/src/lib/pipes/match-score-class.pipe';
import { MatchScoreNormalizedPipe } from '../../../../../../libs/shared/src/lib/pipes/match-score-normalized.pipe';
import { StatusClassPipe } from '../../../../../../libs/shared/src/lib/pipes/status-class.pipe';
import { StatusIconPipe } from '../../../../../../libs/shared/src/lib/pipes/status-icon.pipe';
import { RankClassPipe } from '../../../../../../libs/shared/src/lib/pipes/rank-class.pipe';
import { CurrencyDisplayPipe } from '../../utils/currency-display.pipe';
import { formatDateLocalized, formatDateTimeLocalized } from '../../utils/date-utils';

export interface JobDetailCardData extends RecommendedJob {
  location?: string[];
  // For applied jobs
  applicationStatus?: {
    statusName: string;
  };
  appliedDate?: string;
  assignedBy?: {
    firstName: string;
    lastName: string;
  };
  // Saved jobs data
  experience?: string;
}

@Component({
  selector: 'app-job-detail-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    RouterModule,
    ButtonComponent,
    RankLabelPipe,
    MatchScoreClassPipe,
    MatchScoreNormalizedPipe,
    StatusClassPipe,
    StatusIconPipe,
    RankClassPipe,
    CurrencyDisplayPipe
  ],
  templateUrl: './job-detail-card.component.html',
  styleUrls: ['./job-detail-card.component.scss']
})
export class JobDetailCardComponent {
  @Input() job!: JobDetailCardData;
  @Input() mode: 'applied' | 'saved' | 'browse' | 'recommended' = 'applied';

  @HostBinding('class.mode-browse') get isBrowseMode(): boolean {
    return this.mode === 'browse';
  }
  @Input() isUnsaving = false;
  @Input() isSaving = false;
  @Input() isLoggedIn = false;
  @Input() jobDetailRoute = '';
  @Input() jobQueryParams: { [key: string]: any } = {};

  @Output() unsaveJob = new EventEmitter<string>();
  @Output() saveJob = new EventEmitter<string>();

  constructor(private readonly translocoService: TranslocoService) {}

  get jobDetailAriaLabel(): string {
    const title = this.job?.jobTitle || 'Unknown Job';
    const company = this.job?.tenantName;

    return company ? `${title} - ${company}` : title;
  }

  onUnsaveClick(event: Event): void {
    event.stopPropagation();
    this.unsaveJob.emit(String(this.job.id));
  }

  onSaveClick(event: Event): void {
    event.stopPropagation();
    this.saveJob.emit(String(this.job.id));
  }

  hasRankBadge(): boolean {
    return this.mode === 'recommended' && !!this.job.rank && this.job.rank <= 3;
  }

  formatDate(dateInput: string | number | Date | undefined): string {
    return formatDateLocalized(dateInput, this.translocoService.getActiveLang());
  }

  formatDateTime(dateInput: string | number | Date | undefined): string {
    return formatDateTimeLocalized(dateInput, this.translocoService.getActiveLang());
  }
}
